import { Box, Stack, Typography } from '@mui/material';
import { type ObjectLiteral, useSubmission, useUpdatePolicyData } from 'api/submission';
import {
  APPLICATION_STEPS,
  PIP_MEDICAL_CHOICES_DESCRIPTION,
  POL_PIP_MI_TOTAL_MEDICAL_EXPENSES_DEFAULT,
} from 'common/constants';
import { displayBackendErrorMessage } from 'components/DisplayBackEndErrorMessage';
import {
  type Field,
  QuestionEngine,
  type QuestionEngineOnChangeProps,
  useQuestionEngine,
} from 'components/lib/QuestionEngine';
import { StepFooterActions } from 'components/StepFooterActions';
import { isEmpty, isEqual, pick } from 'lodash-es';
import { type FC, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useFullscreenLoader } from 'store/features/UI';

const {
  label,
  fields: initialFields,
}: {
  label: string;
  fields: Field[];
} = APPLICATION_STEPS.vehicles_injury_protection;

const VehiclesPersonalInjuryProtection: FC = () => {
  const { id: submissionId } = useParams() as { id: string };
  const { data: submission } = useSubmission(submissionId);
  const { setFullscreenLoading } = useFullscreenLoader();

  const { mutateAsync: updatePolicyData } = useUpdatePolicyData(submissionId);

  const { validateFormik, updateState, ...questionEngineProps } = useQuestionEngine({
    fields: initialFields,
  });
  const { state, ref: questionEngineRef } = questionEngineProps;

  const submissionState: ObjectLiteral = {
    ...submission?.data.policy_data,
  };

  // When backend submission data changed, sync it to question engine state
  useEffect(() => {
    if (!isEmpty(submission?.data)) {
      updateState(submissionState);
    }
  }, [submission?.data]);

  const handleNext = async () => {
    const isValid = await validateFormik();

    if (!isValid) {
      return false;
    }

    const renderedQuestionCodes =
      questionEngineRef?.current?.renderedQuestions()?.map((question) => question.code) || [];

    const renderedQuestionsState = pick(state, renderedQuestionCodes);

    /**
     * if the backend submission state is same as the question engine state,
     * let changing the step without saving
     */
    if (isEqual(renderedQuestionsState, pick(submissionState, renderedQuestionCodes))) {
      return true;
    }

    const body = {
      ...submissionState,
      ...{
        ...renderedQuestionsState,
        pol_pip_mi_excess_attendant_care:
          renderedQuestionsState.pol_pip_mi_total_medical_expenses ===
          POL_PIP_MI_TOTAL_MEDICAL_EXPENSES_DEFAULT
            ? ''
            : renderedQuestionsState.pol_pip_mi_excess_attendant_care,
      },
    };

    try {
      setFullscreenLoading(true, 'Saving the answers');

      await updatePolicyData({
        id: submissionId,
        body,
      });

      return true;
    } catch (_error) {
      displayBackendErrorMessage(_error);
      return false;
    } finally {
      setFullscreenLoading(false);
    }
  };

  const onChangeEmitted = async (data: QuestionEngineOnChangeProps) => {
    const { field, value, oldValue } = data || {};

    switch (field) {
      case 'pol_pip_mi_total_medical_expenses': {
        if (
          value !== POL_PIP_MI_TOTAL_MEDICAL_EXPENSES_DEFAULT &&
          oldValue === POL_PIP_MI_TOTAL_MEDICAL_EXPENSES_DEFAULT &&
          isEmpty(state?.pol_pip_mi_excess_attendant_care)
        ) {
          updateState({
            ...state,
            pol_pip_mi_total_medical_expenses: value,
            pol_pip_mi_excess_attendant_care: '0',
          });
        }
      }
    }
  };

  return (
    <Stack>
      <Typography variant="h1" sx={{ mb: 3 }}>
        {label}
      </Typography>

      <Stack mb={3} gap={2}>
        <Typography variant="h2">
          Your PIP Medical Choices and the Risks and Benefits of Each
        </Typography>

        {PIP_MEDICAL_CHOICES_DESCRIPTION.map((option) => (
          <Stack gap={0.5} key={option.title}>
            <Typography variant="h3">{option.title}</Typography>
            <Typography>{option.description}</Typography>
            {option.note && (
              <Typography>
                <Box component="span" fontWeight={700}>
                  NOTE:
                </Box>{' '}
                {option.note}
              </Typography>
            )}
            <Typography>
              <Box component="span" fontWeight={700}>
                Risks:
              </Box>{' '}
              {option.risks}
            </Typography>
            <Typography>
              <Box component="span" fontWeight={700}>
                Benefits:
              </Box>{' '}
              {option.benefits}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <QuestionEngine {...questionEngineProps} onChangeEmitted={onChangeEmitted} />

      <StepFooterActions onNext={handleNext} />
    </Stack>
  );
};

export default VehiclesPersonalInjuryProtection;
